import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ACHIEVEMENT_TIERS,
  AchievementTier
} from '../lib/achievementsData';
import {
  UserAchievementItem,
  getUserAchievements,
  claimAchievementReward
} from '../lib/gameEngine';
import './AchievementsMenu.css';

interface AchievementsMenuProps {
  onBack: () => void;
  onWalletUpdated?: (newWallet: number) => void;
}

type FilterTier = 'all' | AchievementTier;

const FILTER_TABS: { id: FilterTier; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'common', label: 'Common' },
  { id: 'uncommon', label: 'Uncommon' },
  { id: 'scarce', label: 'Scarce' },
  { id: 'rare', label: 'Rare' },
  { id: 'ultra_rare', label: 'Ultra Rare' },
  { id: 'secret', label: 'Secret' },
];

export const AchievementsMenu: React.FC<AchievementsMenuProps> = ({
  onBack,
  onWalletUpdated
}) => {
  const [selectedFilter, setSelectedFilter] = useState<FilterTier>('all');
  const [achievements, setAchievements] = useState<UserAchievementItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load authoritative achievements directly from PostgreSQL
  const loadAchievements = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getUserAchievements();
      setAchievements(data);
    } catch (e) {
      console.error('Failed to load achievements:', e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAchievements();
  }, [loadAchievements]);

  // Handle claiming unlocked rewards
  const handleClaim = async (achievement: UserAchievementItem) => {
    if (claimingId || achievement.is_claimed || !achievement.is_unlocked) return;
    setClaimingId(achievement.id);
    setFeedbackMessage(null);

    try {
      const res = await claimAchievementReward(achievement.id);
      if (res.success) {
        if (res.newWallet !== undefined && onWalletUpdated) {
          onWalletUpdated(res.newWallet);
        }
        setFeedbackMessage({
          type: 'success',
          text: `Claimed +€${res.rewardAmount?.toLocaleString()} for "${res.achievementName || achievement.name}"!`
        });
        // Optimistically mark as claimed
        setAchievements(prev =>
          prev.map(a => a.id === achievement.id ? { ...a, is_claimed: true } : a)
        );
      } else {
        setFeedbackMessage({
          type: 'error',
          text: res.error || 'Failed to claim achievement reward'
        });
      }
    } catch (err: any) {
      setFeedbackMessage({
        type: 'error',
        text: err?.message || 'Error claiming achievement reward'
      });
    } finally {
      setClaimingId(null);
    }
  };

  const ownedCount = useMemo(() => {
    return achievements.filter((a) => a.is_unlocked).length;
  }, [achievements]);

  const totalCount = achievements.length;

  const filteredAchievements = useMemo(() => {
    if (selectedFilter === 'all') {
      return achievements;
    }
    return achievements.filter((item) => item.tier === selectedFilter);
  }, [selectedFilter, achievements]);

  return (
    <div className="achievements-container">
      {/* Navigation */}
      <div className="achievements-nav">
        <button className="back-btn" onClick={onBack}>
          ← Back
        </button>
      </div>

      {/* Header & Owned Count Label */}
      <div className="achievements-header">
        <h1 className="achievements-title">Achievements</h1>
        <div className="achievements-owned-label">
          Owned: {ownedCount} / {totalCount}
        </div>
      </div>

      {/* Notification Banner (Errors only) */}
      {feedbackMessage && feedbackMessage.type === 'error' && (
        <div style={{
          backgroundColor: '#fee2e2',
          border: '1.5px solid #dc2626',
          color: '#b91c1c',
          padding: '10px 16px',
          borderRadius: '8px',
          marginBottom: '16px',
          fontSize: '14px',
          fontWeight: '600',
          textAlign: 'center',
          width: '100%'
        }}>
          ⚠️ {feedbackMessage.text}
        </div>
      )}

      {/* Tier Filter Tabs */}
      <div className="achievements-filter-tabs">
        {FILTER_TABS.map((tab) => {
          const count =
            tab.id === 'all'
              ? achievements.length
              : achievements.filter((a) => a.tier === tab.id).length;

          if (count === 0 && tab.id !== 'all') return null;

          return (
            <button
              key={tab.id}
              className={`achievements-filter-btn ${
                selectedFilter === tab.id ? 'active' : ''
              }`}
              onClick={() => setSelectedFilter(tab.id)}
            >
              {tab.label} <span className="tab-count">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="achievements-loading">
          <div className="achievements-spinner" />
          <span>Loading achievements from server...</span>
        </div>
      ) : (
        /* 3-Wide Grid */
        <div className="achievements-grid">
          {filteredAchievements.map((achievement) => {
            const tierConfig = ACHIEVEMENT_TIERS[achievement.tier] || {
              label: achievement.tier,
              reward: achievement.reward_amount
            };
            const percent = Math.min(
              100,
              achievement.max_progress > 0
                ? Math.round((achievement.current_progress / achievement.max_progress) * 100)
                : 0
            );

            return (
              <div
                key={achievement.id}
                className={`achievement-card tier-${achievement.tier} ${
                  achievement.is_unlocked ? 'achievement-owned' : 'achievement-locked'
                }`}
              >
                <div className="achievement-card-top">
                  {/* Concentric Circle Badge */}
                  <div className={`achievement-circle-badge badge-tier-${achievement.tier}`}>
                    <div className="badge-outer-circle">
                      <div className="badge-inner-circle">
                        <span className="badge-symbol badge-symbol-text">
                          {achievement.symbol}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="achievement-card-header-info">
                    <h3 className="achievement-card-title">{achievement.name}</h3>
                    <div className="achievement-tags-row">
                      <span className={`achievement-tier-pill tier-${achievement.tier}`}>
                        {tierConfig.label}
                      </span>
                      <span className="achievement-reward-pill">
                        +€{achievement.reward_amount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="achievement-card-body">
                  <p className="achievement-card-desc">{achievement.description}</p>
                </div>

                {/* Progress Bar Section */}
                <div className="achievement-progress-section">
                  <div className="achievement-progress-labels">
                    <span className="progress-fraction">
                      {achievement.max_progress > 1
                        ? `${achievement.current_progress.toLocaleString()} / ${achievement.max_progress.toLocaleString()}`
                        : achievement.is_unlocked
                        ? 'Completed'
                        : '0 / 1'}
                    </span>
                    <span className="progress-percent">{percent}%</span>
                  </div>
                  <div className="achievement-progress-track">
                    <div
                      className={`achievement-progress-fill fill-tier-${achievement.tier} ${
                        achievement.is_unlocked ? 'fill-completed' : ''
                      }`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>

                {/* Claim / Claimed Action Row */}
                {achievement.is_unlocked && (
                  <div className="achievement-claim-row">
                    {achievement.is_claimed ? (
                      <span className="achievement-claimed-badge">✓ Claimed</span>
                    ) : (
                      <button
                        className="achievement-claim-btn"
                        onClick={() => handleClaim(achievement)}
                        disabled={claimingId === achievement.id}
                      >
                        {claimingId === achievement.id
                          ? 'Claiming...'
                          : `Claim +€${achievement.reward_amount.toLocaleString()}`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
